import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, HandCoins } from "lucide-react";
import { fmtCurrency } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";

import { nzNow } from "@/lib/dates";
const inputCls = "w-full rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-2.5 px-3 text-sm text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] transition-colors duration-200";

export default function Funding({ api, year, month, record, settings, reload }) {
  const { siteScope } = useAuth();
  const editBlocked = settings?.financeSiteMode === "per_site" && !siteScope;
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [subsidyAmt, setSubsidyAmt] = useState(null);

  useEffect(() => {
    setRows((record?.funding || []).map((f) => ({ ...f, amount: String(f.amount) })));
  }, [month, year, record]);

  useEffect(() => {
    axios.get(`${api}/invoices/annual`, { params: { year } })
      .then((r) => setSubsidyAmt((r.data.months || []).find((m) => m.label === month)?.subsidyAmt ?? 0))
      .catch(() => setSubsidyAmt(null));
  }, [api, year, month]);

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const update = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () => setRows([...rows, { id: Date.now().toString(36), source: "", amount: "", date: "", notes: "" }]);
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${api}/months/${year}/${month}/funding`, { items: rows.map((r) => ({ ...r, amount: parseFloat(r.amount) || 0 })) });
      toast.success(`Funding saved for ${month} ${year}`);
      await reload();
    } catch (e) { toast.error("Failed to save funding"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="funding-section">
      <div className="px-6 py-4 border-b border-[var(--hp-border)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HandCoins className="h-5 w-5 text-[var(--hp-accent)]" />
          <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Funding Income — {month} {year}</h3>
        </div>
        <span className="text-xs text-[var(--hp-muted)]">Total <span data-testid="funding-total" className="font-mono-num font-bold text-[var(--hp-accent)]">{fmtCurrency(total)}</span></span>
      </div>
      {!!subsidyAmt && (
        <p className="px-6 pt-3 text-xs text-[var(--hp-muted)]" data-testid="funding-subsidy-reconcile">
          For comparison: <span className="font-mono-num text-[var(--hp-accent)]">{fmtCurrency(subsidyAmt)}</span> in per-child MSD OSCAR subsidy was applied to family invoices this month (Finance → Invoicing) — worth checking against what you record here.
        </p>
      )}
      <div className="px-6 py-5 space-y-3">
        {editBlocked && (
          <div className="rounded-xl border border-[var(--hp-gold)]/40 bg-[var(--hp-gold)]/5 px-4 py-3 text-xs text-[var(--hp-heading)]" data-testid="funding-combined-notice">
            Funding is entered per site — this is a read-only combined total across every site. Pick a specific site above to edit it.
          </div>
        )}
        <fieldset disabled={editBlocked} className="space-y-3">
        <p className="text-xs text-[var(--hp-muted)]">MSD / OSCAR subsidies, grants and other funding. This is a separate record for tracking what's been received against what's expected below — it does not add to the Income figures shown on the Finance Dashboard, which are based only on invoiced family bookings.</p>
        {rows.length === 0 && (
          <p className="text-xs text-[var(--hp-muted)] rounded-xl border border-dashed border-[var(--hp-border)] px-3 py-2.5" data-testid="funding-empty">No funding recorded this month.</p>
        )}
        {rows.map((r, i) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2" data-testid={`funding-row-${i}`}>
            <input data-testid={`funding-source-${i}`} value={r.source} onChange={(e) => update(i, { source: e.target.value })} placeholder="Source e.g. MSD OSCAR subsidy" className={`${inputCls} min-w-[12rem] flex-1 basis-56`} />
            <div className="relative w-32 shrink-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hp-muted)] font-mono-num text-sm">$</span>
              <input data-testid={`funding-amount-${i}`} type="number" step="0.01" value={r.amount} onChange={(e) => update(i, { amount: e.target.value })} placeholder="0.00" className={`${inputCls} font-mono-num pl-6`} />
            </div>
            <input data-testid={`funding-date-${i}`} type="date" value={r.date} onChange={(e) => update(i, { date: e.target.value })} className={`${inputCls} w-44 shrink-0`} />
            <input data-testid={`funding-notes-${i}`} value={r.notes} onChange={(e) => update(i, { notes: e.target.value })} placeholder="Note" className={`${inputCls} min-w-[10rem] flex-1 basis-40`} />
            <button data-testid={`funding-remove-${i}`} onClick={() => remove(i)} aria-label="Remove funding" className="grid place-items-center h-8 w-8 shrink-0 rounded-lg text-[var(--hp-muted)] hover:text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button data-testid="funding-save-button" onClick={save} disabled={saving || editBlocked}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--hp-primary)] px-5 py-2.5 text-sm font-bold text-[#04231a] hover:bg-[var(--hp-primary-hover)] transition-colors duration-200 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Funding"}
          </button>
          <button data-testid="funding-add-row" onClick={add}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-5 py-2.5 text-sm font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors">
            <Plus className="h-4 w-4" /> Add funding
          </button>
        </div>
        </fieldset>
      </div>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function FundingCard({ api, year, months, settings, reload }) {
  const [month, setMonth] = useState(MONTHS[nzNow().getMonth()]);
  const record = (months || []).find((m) => m.month === month);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">Month</label>
        <select data-testid="funding-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)]">
          {MONTHS.map((m) => <option key={m} value={m}>{m} {year}</option>)}
        </select>
      </div>
      <Funding api={api} year={year} month={month} record={record} settings={settings} reload={reload} />
    </div>
  );
}
